import i18n, { ResourceLanguage } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { EXTENSIONS } from 'probonio-shared-ui/registry';
import { initReactI18next } from 'react-i18next';
import { isDevelopment } from '../constants';
import '../extension'; // Ensure extensions are registered before loading translations. This is done here and not in the main file to avoid race conditions with file loading.
import { TranslationExtensionInterface } from '../extension/interface/translation.extension.interface';
import de from './de.json';
import en from './en.json';
import hr from './hr.json';
import hu from './hu.json';
import pl from './pl.json';
import ro from './ro.json';
import tr from './tr.json';

export enum LocaleEnum {
  DE = 'de',
  EN = 'en',
  HU = 'hu',
  HR = 'hr',
  PL = 'pl',
  RO = 'ro',
  TR = 'tr',
}

export const LANGUAGE_NAMES: Record<LocaleEnum, string> = {
  en: 'English',
  de: 'Deutsch',
  ro: 'Română (Beta)',
  hu: 'Magyar (Beta)',
  pl: 'Polski (Beta)',
  hr: 'Hrvatski (Beta)',
  tr: 'Türkçe (Beta)',
};

function registerCustomFormatters(instance: typeof i18n): void {
  const formatter = instance.services.formatter;
  if (!formatter) {
    return;
  }

  formatter.addCached('time', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      if (!(value instanceof Date)) {
        return value;
      }
      return value.toLocaleDateString(locale ?? undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    };
  });

  formatter.addCached('datetime', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      if (!(value instanceof Date)) {
        return value;
      }
      return value.toLocaleDateString(locale ?? undefined, {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };
  });

  formatter.addCached('monthAndYear', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      if (!(value instanceof Date)) {
        return value;
      }
      return value.toLocaleDateString(locale ?? undefined, { year: 'numeric', month: 'long' });
    };
  });

  // Legacy `else` for Date: short numeric date (day / month / year). Used as `{{…, date}}`.
  formatter.addCached('date', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      if (!(value instanceof Date)) {
        return value;
      }
      return value.toLocaleDateString(locale ?? undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };
  });

  formatter.addCached('euro', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      if (typeof value !== 'number') {
        return value;
      }
      const centToEuro = (value / 100).toLocaleString(locale ?? undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      if (locale?.startsWith('en')) {
        return `€${centToEuro}`;
      }
      return `${centToEuro}€`;
    };
  });

  formatter.addCached('euro-float', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      if (typeof value !== 'number') {
        return value;
      }
      const centToEuro = (value / 100).toLocaleString(locale ?? undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      if (locale?.startsWith('en')) {
        return `€${centToEuro.replace(/[.,]00$/, '')}`;
      }
      return `${centToEuro.replace(/[.,]00$/, '')}€`;
    };
  });
}

function extensionTranslations(): ResourceLanguage {
  return EXTENSIONS.getKeyedProviders(TranslationExtensionInterface).reduce((merged: ResourceLanguage, extension) => {
    merged[extension.provider.moduleName] = extension.provider.translations;

    return merged;
  }, {});
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      de: isDevelopment ? { ...de, extensions: extensionTranslations() } : de,
      en,
      hr,
      hu,
      pl,
      ro,
      tr,
    },
    fallbackLng: 'de',
    interpolation: {
      escapeValue: false,
    },
    react: {
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 'ul', 'ol', 'li'],
    },
    debug: import.meta.env.DEV,
  })
  .then(() => {
    registerCustomFormatters(i18n);
  });

i18n.on('languageChanged', lng => {
  document.documentElement.setAttribute('lang', lng);
});

function hotReload(lang: string): void {
  const file = `./${lang}.ts`;
  if (import.meta.hot) {
    import.meta.hot.accept(file, updatedModule => {
      if (!updatedModule) {
        return;
      }
      const resources = updatedModule.default;
      Object.keys(resources).forEach(namespace => {
        void i18n.addResourceBundle(lang, namespace, resources[namespace], true, true);
      });
    });
  }
}

Object.values(LocaleEnum).forEach(locale => {
  hotReload(locale);
});

export default i18n;
